import { figureBase, figureBakes } from 'virtual:figure-bakes';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import { decodeFigure, figureKey, type FigureTape } from './figure-tape.js';
import type { HeadCacheEntries, HeadDetail } from './human-head.js';
import { worldFigureOptions } from './create-unit-animator.js';
import type { FigureBakeSource } from './head-pool.js';

/**
 * The figures baked at build time (`scripts/figure-bakes.mjs`): every resident and enemy of the game's content, at each Character detail
 * level, with its head surfaces and its body's tape (`figure-tape.ts`). A build bakes them from its own code; a dev server lists only the
 * bakes of the code it serves, re-baking stale ones in the background. A figure that is not listed (a hero, a scenario's own crew) is
 * meshed live as before.
 */
export interface FigureBake { key: string; heads: HeadCacheEntries; tape: FigureTape }

const bakedKey = (definition: UnitDefinition, detail: HeadDetail) => {
  const key = figureKey(definition, worldFigureOptions(detail));
  return figureBakes[detail]?.[key] && typeof DecompressionStream !== 'undefined' ? key : null;
};
/** The world figures' bakes, for `stockFigureHeads`. */
export const bakedFigures: FigureBakeSource = { key: bakedKey, load: loadFigureBake };

/** The bake of `definition` as a world figure at `detail`, or null when there is none or it cannot be read. */
async function loadFigureBake(definition: UnitDefinition, detail: HeadDetail): Promise<FigureBake | null> {
  const key = bakedKey(definition, detail), file = key && figureBakes[detail]?.[key];
  if (!key || !file) return null;
  try {
    const response = await fetch(`${figureBase}${file}`);
    if (!response.ok || !response.body) return null;
    // Baked files are gzip streams of their own, so no server or host has to compress them.
    const buffer = await new Response(response.body.pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
    const { heads, tape } = decodeFigure(buffer);
    return { key, heads: heads as HeadCacheEntries, tape };
  } catch (error) {
    console.warn('A baked figure could not be read; meshing it live.', error);
    return null;
  }
}
